'use client'

import type { UseScrollingIndicatorProps } from '@pearlconvert/ui/components/carousel/carousel.types'

import { useCallback, useEffect, useMemo, useState } from 'react'

export const useScrollingIndicator = ({
  caruselItems,
  carouselRef,
  isLoading,
  noWrap,
  slideCount,
  slideIndex,
  slidesPerView,
}: UseScrollingIndicatorProps) => {
  const fullSlidesPerView = Math.floor(slidesPerView)
  const [scrollbarPosition, setScrollbarPosition] = useState(0)
  const fullSlidesItems = (slideCount - 1) * fullSlidesPerView
  const itemsInLastSlide = Math.min(
    Math.max(0, caruselItems - fullSlidesItems),
    fullSlidesPerView
  )

  const fullSlideWidth = (fullSlidesPerView / caruselItems) * 100
  const lastSlideWidth = (itemsInLastSlide / caruselItems) * 100

  const calculatePosition = useCallback(() => {
    if (noWrap) {
      const carouselContainer = carouselRef?.current
      if (!carouselContainer) {
        return
      }

      const left = carouselContainer.scrollLeft || 0
      const offset =
        (carouselContainer.scrollWidth || 0) -
        (carouselContainer.clientWidth || 0)

      const position = (left / offset) * (slideCount - 1) * (100 / slideCount)

      setScrollbarPosition(position)
    } else {
      let position = slideIndex * fullSlideWidth

      if (slideCount - 1 === slideIndex) {
        position = 100 - lastSlideWidth
      }

      setScrollbarPosition(position)
    }
  }, [
    carouselRef,
    fullSlideWidth,
    lastSlideWidth,
    noWrap,
    slideCount,
    slideIndex,
  ])

  useEffect(() => {
    const carouselContainer = carouselRef?.current

    // Debouncing Scroll Event
    let animationFrameId: number

    const handleScroll = () => {
      cancelAnimationFrame(animationFrameId)
      animationFrameId = requestAnimationFrame(() => {
        calculatePosition()
      })
    }

    if (noWrap && carouselContainer) {
      carouselContainer.addEventListener('scroll', handleScroll)
      carouselContainer.addEventListener('touchmove', handleScroll)
    } else {
      calculatePosition()
    }

    return () => {
      if (noWrap && carouselContainer) {
        carouselContainer.removeEventListener('scroll', handleScroll)
        carouselContainer.removeEventListener('touchmove', handleScroll)
      }
      cancelAnimationFrame(animationFrameId)
    }
  }, [calculatePosition, carouselRef, noWrap])

  const indicatorWidth = useMemo(() => {
    if (isLoading) {
      return 0
    }

    return noWrap
      ? 100 / slideCount
      : slideIndex === slideCount - 1
        ? lastSlideWidth
        : fullSlideWidth
  }, [
    fullSlideWidth,
    isLoading,
    lastSlideWidth,
    noWrap,
    slideCount,
    slideIndex,
  ])

  return { fullSlideWidth, indicatorWidth, lastSlideWidth, scrollbarPosition }
}
